import { useState } from 'react';
import { Check } from 'lucide-react';
import { PLATFORM_META, type Platform } from '../lib/platforms';

type AccountAvatarAccount = {
  platform: Platform;
  username?: string | null;
  displayName?: string | null;
  profilePhotoUrl?: string | null;
};

type AccountAvatarProps = {
  account: AccountAvatarAccount;
  size?: number;
  selected?: boolean;
};

export function AccountAvatar({ account, size = 40, selected = false }: AccountAvatarProps) {
  const [imgError, setImgError] = useState(false);
  const meta = PLATFORM_META[account.platform];
  const Icon = meta.icon;

  const badgeSize = Math.round(size / 3);
  const badgeOffset = Math.round(badgeSize * 0.25);

  const hasPhoto = Boolean(account.profilePhotoUrl) && !imgError;

  return (
    <span
      className="relative inline-flex shrink-0"
      style={{ width: size, height: size }}
    >
      {hasPhoto ? (
        <img
          src={account.profilePhotoUrl!}
          alt={account.displayName ?? account.username ?? meta.label}
          className="rounded-full object-cover w-full h-full"
          onError={() => setImgError(true)}
        />
      ) : (
        <span
          className="inline-flex w-full h-full items-center justify-center rounded-lg text-white"
          style={{ backgroundColor: meta.color }}
        >
          <Icon size={Math.round(size * 0.5)} color="white" />
        </span>
      )}

      {/* Platform badge */}
      {hasPhoto && (
        <span
          className="absolute flex items-center justify-center rounded-full ring-2 ring-white"
          style={{
            width: badgeSize,
            height: badgeSize,
            bottom: -badgeOffset,
            right: -badgeOffset,
            backgroundColor: meta.color,
          }}
        >
          <Icon size={Math.round(badgeSize * 0.55)} color="white" />
        </span>
      )}

      {/* Selected check badge */}
      {selected && (
        <span className="absolute -top-1 -right-1 inline-flex h-4 w-4 items-center justify-center rounded-full bg-brand-600 text-white ring-2 ring-white z-10">
          <Check size={9} strokeWidth={4} />
        </span>
      )}
    </span>
  );
}
